import React from "react";
import { Box, TextField, FormControl, Autocomplete } from "@mui/material";

const getOptionLabelGeneric = (option) => {
  if (option == null) return "";
  if (typeof option === "string") return option;
  return option.text ?? option.label ?? option.value ?? "";
};

const DnsRecordForm = ({
  fields = [],
  formData,
  setFormData,
  dropdownData,
  formSubmitted,
  apiValidationFields = [],
  setApiValidationFields
}) => {
  const hostedZoneOptions = dropdownData.HostedZones || [];
  const recordTypeOptions = dropdownData.RecordType || [];

  const selectedHostedZone = hostedZoneOptions.find(
    (item) => String(item.value) === String(formData.HostedZones?.value)
  );

  const selectedHostedZoneText =
    selectedHostedZone?.text || selectedHostedZone?.label || "";

  const selectedRecordType = recordTypeOptions.find(
    (item) => String(item.value) === String(formData.RecordType?.value)
  );

  const selectedRecordTypeCode = selectedRecordType?.code || "";
  const selectedValuePlaceholder =
    selectedRecordType?.placeholderValue || ""; 
  const selectedValidationPattern =
    selectedRecordType?.validationPattern || "";

  const recordNameValue = formData.RecordName?.value || "";

  const fullRecordName =
    recordNameValue && selectedHostedZoneText
      ? `${recordNameValue}.${selectedHostedZoneText}`.replace(/\.\.+/g, ".")
      : recordNameValue || selectedHostedZoneText || "";

  const validateValueByRecordType = (value, pattern) => {
    if (!value || !pattern) return true;

    try {
      const regex = new RegExp(pattern);
      return regex.test(value);
    } catch (err) {
      console.error("Invalid regex pattern:", err);
      return true;
    }
  };

  const handleFieldChange = (fieldName, value, label = "") => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [fieldName]: {
          value,
          label: label || value,
        },
      };

      if (fieldName === "RecordType") {
        newData.RecordValues  = { value: "", label: "" };
      }

      return newData;
    });
  };

  const route53Fields = fields.filter((field) =>
    [
      "HostedZones",
      "RecordName",
      "RecordType",
      "Alias",
      "RecordValues",
      "TTL",
      "RoutingPolicy",
    ].includes(field.fieldName)
  );

  const renderField = (field) => {
    const isRequiredError =
      formSubmitted &&
      field.isRequired &&
      !formData[field.fieldName]?.value;

    if (field.fieldType === "dropdown") {
      const options = dropdownData[field.fieldName] || [];
      const currentVal = formData[field.fieldName]?.value;

      return (
        <FormControl sx={{ mt: 1, width: "100%" }} key={field.fieldId}>
          <Autocomplete
            options={options}
            value={
              options.find((o) => String(o.value) === String(currentVal)) || null
            }
            getOptionLabel={getOptionLabelGeneric}
            isOptionEqualToValue={(opt, val) =>
              String(opt.value) === String(val?.value)
            }
            onChange={(e, newValue) => {
              handleFieldChange(
                field.fieldName,
                newValue?.value ?? "",
                newValue?.label ?? newValue?.text ?? ""
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name={field.fieldName}
                label={field.fieldLabel}
                required={field.isRequired}
                error={isRequiredError}
                helperText={isRequiredError ? "This field is required" : ""}
              />
            )}
          />
        </FormControl>
      );
    }

    

   if (field.fieldType === "text") {
  const isValueField = field.fieldName === "RecordValues";

//   console.log("Field Name UI:", field.fieldName);
// console.log("API Validation Fields:", apiValidationFields);
  const isRecordNameField = field.fieldName === "RecordName";
  const isTTLField = field.fieldName === "TTL";

  const currentValue = formData[field.fieldName]?.value || "";

 
const recordNameApiError =
  isRecordNameField &&
  apiValidationFields
    .map(f => f.toLowerCase())
    .includes(field.fieldName.toLowerCase());



  const valuePatternError =
    isValueField &&
    formData.RecordValues?.value &&
    !validateValueByRecordType(
      formData.RecordValues.value,
      selectedValidationPattern
    );

  const ttlNumber = Number(currentValue);
  const ttlRangeError =
    isTTLField &&
    currentValue !== "" &&
    (!/^\d+$/.test(currentValue) ||ttlNumber < 0 || ttlNumber > 172800);

  return (
    <TextField
      key={field.fieldId}
      name={field.fieldName}
      label={field.fieldLabel}
      required={field.isRequired}
      value={currentValue}
      onChange={(e) => {
        let value = e.target.value;

        if (isTTLField) {
          value = value.replace(/\D/g, "");
          
 // Remove leading zeros (new behavior)
  value = value.replace(/^0+(?=\d)/, "");

        }

        
if (isRecordNameField || isValueField) {
    value = value.replace(/\s/g, ""); //  remove all spaces
  }


 if (isRecordNameField) {
    setApiValidationFields(prev =>
      prev.filter(f => f.toLowerCase() !== "recordname")
    );
  }

        handleFieldChange(field.fieldName, value, value);
      }}
      placeholder={
        isValueField
          ? selectedValuePlaceholder || field.placeholder || ""
          : isTTLField
          ? "60 to 172800"
          : field.placeholder || ""
      }
      margin="normal"
      error={isRequiredError || valuePatternError || ttlRangeError || recordNameApiError }
      helperText={
        isRequiredError
          ? "This field is required"         
  : recordNameApiError
    ? "Record name already exists"
          : valuePatternError
          ? `Invalid value for ${selectedRecordTypeCode || "selected"} record type`
          : ttlRangeError
          ? "TTL must be a number between 60 and 172800"
          : isTTLField
          ? "Recommended values: 60 to 172800 (two days)"
          : isRecordNameField && selectedHostedZoneText
          ? `Full record name: ${fullRecordName}`
          : ""
      }
      inputProps={
        isTTLField
          ? { inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }
          : {}
      }
      sx={{ mt: 1, width: "100%" }}
    />
  );
}


    return null;
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
        gap: "16px",
      }}
    >
      {route53Fields
        .filter((field) => field.isDisplay)
        .map((field) => (
          <Box
            key={field.fieldId}
            sx={{
              flex: "1 1 calc(50% - 16px)",
              minWidth: "300px",
            }}
          >
            {renderField(field)}
          </Box>
        ))}
    </Box>
  );
};

export default DnsRecordForm;